import { useState, useEffect, useCallback, useMemo } from 'react';
import { InteractionType } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { faCopy, faCheck, faRotateRight, faHammer, faLock } from '@fortawesome/free-solid-svg-icons';
import Hero from '../../common/layout/Hero';
import HeroBody from '../../common/layout/HeroBody';
import Container from '../../common/layout/Container';
import Icon from '../../common/elements/Icon';
import Button from '../../common/elements/Button';
import SmallLoadingSpinner from '../../common/elements/SmallLoadingSpinner';
import { thor_api_urls } from '../../common/Constants';
import useFetchWithMsal, { apiScopes } from '../../../useFetchWithMsal';
import '../css/thor.css';

function guessEnvironment() {
    const host = window.location.hostname;
    if (host === 'www.tripadvisor.com') return 'PROD';
    return 'DEV';
}

function readHashParams() {
    const hash = window.location.hash.replace('#', '');
    const params = new URLSearchParams(hash);
    return {
        taUnique: params.get('taUnique') ?? '',
        userId: params.get('userId') ?? '',
        // bucketIds may be comma- or plus-separated
        bucketIds: params.get('bucketIds') ?? '',
        environment: params.get('environment') ?? '',
    };
}

// Given the raw state string (comma or plus separated) return a Set of integer bucket IDs
function parseBucketIds(raw) {
    if (!raw.trim()) return new Set();
    return new Set(
        raw.split(/[,+]/).map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
    );
}

// Port of Retool's flattenExperiment — enriches each entry with prebucket info
function flattenExperiment(obj, selectedBucketIds) {
    let prebucketKey = null;
    let prebucketId = null;
    (obj.bucketDetailsList ?? []).forEach(bucket => {
        if (selectedBucketIds.has(bucket.id)) {
            prebucketKey = bucket.key;
            prebucketId = bucket.id;
        }
    });
    return { ...obj, prebucketKey, prebucketId };
}

const ThorHammerContent = ({ interaction }) => {
    const hashParams = readHashParams();
    // Reuse the app-wide authenticated fetch hook, parameterized by interaction type:
    // redirect when standalone (matches every other API call), popup when embedded in a
    // cross-origin iframe where the redirect flow can't complete.
    const { execute } = useFetchWithMsal(interaction);
    const [taUnique, setTaUnique] = useState(hashParams.taUnique);
    const [userId, setUserId] = useState(hashParams.userId);
    const [bucketIds, setBucketIds] = useState(hashParams.bucketIds);
    const [environment, setEnvironment] = useState(hashParams.environment || guessEnvironment());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [experiments, setExperiments] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [editKey, setEditKey] = useState(null);
    const [editBucketId, setEditBucketId] = useState('');
    const [actionLoading, setActionLoading] = useState({});
    const [toast, setToast] = useState(null);
    const [stateCopied, setStateCopied] = useState(false);
    const [taCopied, setTaCopied] = useState(false);
    const [userCopied, setUserCopied] = useState(false);

    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 3500);
            return () => clearTimeout(t);
        }
    }, [toast]);

    const showToast = (message, type = 'info') => setToast({ message, type });

    // Parse bucketIds string → Set<int> for prebucket matching
    const selectedBucketIds = useMemo(() => parseBucketIds(bucketIds), [bucketIds]);

    // Enrich experiments with prebucket info derived from the user's current state
    const flattenedExperiments = useMemo(
        () => experiments.map(exp => flattenExperiment(exp, selectedBucketIds)),
        [experiments, selectedBucketIds]
    );

    const overrideCount = flattenedExperiments.filter(e => e.forcedBucketDetails != null).length;

    const filteredExperiments = flattenedExperiments.filter(exp => {
        if (!searchFilter.trim()) return true;
        const f = searchFilter.toLowerCase();
        return exp.experiment.key.toLowerCase().includes(f) ||
            exp.experiment.name.toLowerCase().includes(f) ||
            (exp.forcedBucketDetails?.bucketKey ?? '').toLowerCase().includes(f) ||
            (exp.prebucketKey ?? '').toLowerCase().includes(f);
    });

    const loadExperiments = useCallback(async (ta = taUnique, uid = userId, env = environment) => {
        setLoading(true);
        setError(null);
        try {
            const data = await execute(
                'GET',
                thor_api_urls.hammerExperiments(env, uid.trim(), ta.trim())
            );
            setExperiments(data?.experiments ?? []);
            setLoaded(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [taUnique, userId, environment, execute]);

    // Auto-load once auth is ready — execute's identity changes when the token resolves.
    useEffect(() => {
        loadExperiments(hashParams.taUnique, hashParams.userId, environment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [execute]);

    const upsertForcedBucket = async (experimentKey, bucketId) => {
        const id = parseInt(bucketId, 10);
        setActionLoading(prev => ({ ...prev, [experimentKey]: true }));
        try {
            await execute(
                'POST',
                thor_api_urls.hammerUpsertForcedBucket(experimentKey, id),
                { userId: userId.trim(), taUnique: taUnique.trim() }
            );
            setExperiments(prev => prev.map(e => {
                if (e.experiment.key !== experimentKey) return e;
                const bucket = (e.bucketDetailsList ?? []).find(b => b.id === id);
                return {
                    ...e,
                    forcedBucketDetails: {
                        bucketId: id,
                        bucketKey: bucket?.key ?? String(id),
                        experimentKey,
                        userId: userId.trim(),
                    }
                };
            }));
            setEditKey(null);
        } catch (e) {
            showToast(`Failed to set override: ${e.message}`, 'danger');
        } finally {
            setActionLoading(prev => ({ ...prev, [experimentKey]: false }));
        }
    };

    const deleteForcedBucket = async (experimentKey) => {
        setActionLoading(prev => ({ ...prev, [experimentKey]: true }));
        try {
            await execute(
                'DELETE',
                thor_api_urls.hammerDeleteForcedBucket(experimentKey),
                { userId: userId.trim(), taUnique: taUnique.trim() }
            );
            setExperiments(prev => prev.map(e =>
                e.experiment.key === experimentKey ? { ...e, forcedBucketDetails: null } : e
            ));
        } catch (e) {
            showToast(`Failed to clear override: ${e.message}`, 'danger');
        } finally {
            setActionLoading(prev => ({ ...prev, [experimentKey]: false }));
        }
    };

    const clearAll = async () => {
        if (!window.confirm(`Clear all ${overrideCount} override${overrideCount !== 1 ? 's' : ''}?`)) return;
        setLoading(true);
        try {
            await execute(
                'DELETE',
                thor_api_urls.hammerClearAllForcedBuckets(environment),
                { userId: userId.trim(), taUnique: taUnique.trim() }
            );
            setExperiments(prev => prev.map(e => ({ ...e, forcedBucketDetails: null })));
            showToast('All overrides cleared.', 'success');
        } catch (e) {
            showToast(`Failed to clear all: ${e.message}`, 'danger');
        } finally {
            setLoading(false);
        }
    };

    // Hammer normally runs embedded in an iframe (the legacy servlet, or any other embed), and
    // cross-origin iframes don't get clipboard-write permission unless the parent explicitly
    // grants it via allow="clipboard-write" — which we can't control. Fall back to a legacy
    // execCommand copy, and finally to a prompt() the user can copy from manually, since neither
    // is gated by that permission the way navigator.clipboard is.
    const copyValue = async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            return true;
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = value;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const copied = document.execCommand('copy');
            document.body.removeChild(textarea);
            if (!copied) window.prompt('Copy this value:', value);
            return copied;
        }
    };

    // Copy state: forced bucket takes priority, fall back to natural prebucket
    const copyState = async () => {
        const state = flattenedExperiments
            .map(e => e.forcedBucketDetails?.bucketId ?? e.prebucketId)
            .filter(id => id != null)
            .join('+');

        if (!state) {
            showToast('No overrides found!', 'warning');
            return;
        }

        if (await copyValue(state)) {
            setStateCopied(true);
            setTimeout(() => setStateCopied(false), 2000);
        }
    };

    const copyToClipboard = async (value, setCopiedFn) => {
        if (!value) return;
        if (await copyValue(value)) {
            setCopiedFn(true);
            setTimeout(() => setCopiedFn(false), 1500);
        }
    };

    return (
        <>
            <Container>
                <Hero>
                    <HeroBody>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <Icon icon={faHammer} />
                            <p className="title is-1">Hammer</p>
                        </div>
                        <p className="subtitle is-5 has-text-grey">Force experiment bucket overrides for testing.</p>
                    </HeroBody>
                </Hero>

                <div className="notification is-warning is-light py-2 px-4 mb-4" style={{ borderLeft: '4px solid #ffdd57' }}>
                    📣 Changes you make here can take <strong>up to 5 minutes</strong> to appear live.
                </div>

                {/* Identity + current state */}
                <div className="box mb-4">
                    <p className="subtitle is-6 mb-3 has-text-weight-semibold">Identity</p>
                    <div className="columns is-vcentered">
                        <div className="column">
                            <label className="label is-small">TA Unique / Device ID</label>
                            <div className="field has-addons">
                                <div className="control is-expanded">
                                    <input
                                        className="input is-small"
                                        type="text"
                                        placeholder="Optional — shows your forced overrides"
                                        value={taUnique}
                                        onChange={e => setTaUnique(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadExperiments()}
                                    />
                                </div>
                                <div className="control">
                                    <button type="button" className="button is-small is-light"
                                        title="Copy TA Unique" disabled={!taUnique}
                                        onClick={() => copyToClipboard(taUnique, setTaCopied)}>
                                        <Icon icon={taCopied ? faCheck : faCopy} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="column">
                            <label className="label is-small">User ID</label>
                            <div className="field has-addons">
                                <div className="control is-expanded">
                                    <input
                                        className="input is-small"
                                        type="text"
                                        placeholder="Optional"
                                        value={userId}
                                        onChange={e => setUserId(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadExperiments()}
                                    />
                                </div>
                                <div className="control">
                                    <button type="button" className="button is-small is-light"
                                        title="Copy User ID" disabled={!userId}
                                        onClick={() => copyToClipboard(userId, setUserCopied)}>
                                        <Icon icon={userCopied ? faCheck : faCopy} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="column is-narrow">
                            <label className="label is-small">Environment</label>
                            <div className="select is-small">
                                <select
                                    value={environment}
                                    onChange={e => {
                                        const env = e.target.value;
                                        setEnvironment(env);
                                        loadExperiments(taUnique, userId, env);
                                    }}
                                >
                                    <option value="DEV">Dev</option>
                                    <option value="PROD">Prod</option>
                                </select>
                            </div>
                        </div>
                        <div className="column is-narrow" style={{ paddingTop: '1.75rem' }}>
                            <Button className="is-link is-small" onClick={() => loadExperiments()} disabled={loading}>
                                {loading ? <SmallLoadingSpinner /> : <Icon icon={faRotateRight} />}
                                <span>Reload</span>
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <div className="notification is-danger is-light py-2 px-3 mt-2 is-size-7">{error}</div>
                    )}
                </div>

                {loading && !loaded && (
                    <div className="has-text-centered py-6"><SmallLoadingSpinner /></div>
                )}

                {loaded && (
                    <>
                        <div className="level mb-3">
                            <div className="level-left">
                                <div className="level-item">
                                    <input
                                        className="input is-small"
                                        type="text"
                                        placeholder="Search experiments…"
                                        style={{ width: '300px' }}
                                        value={searchFilter}
                                        onChange={e => setSearchFilter(e.target.value)}
                                    />
                                </div>
                                <div className="level-item is-size-7 has-text-grey">
                                    {filteredExperiments.length} of {experiments.length} experiments
                                    {overrideCount > 0 && (
                                        <span className="ml-2 tag is-link is-light is-small">
                                            {overrideCount} override{overrideCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="table-container" style={{ marginBottom: '4.5rem' }}>
                            <table className="table is-fullwidth is-hoverable is-size-7">
                                <thead>
                                    <tr style={{ background: '#36e0a1' }}>
                                        <th style={{ color: '#000' }}>Experiment Name</th>
                                        <th style={{ color: '#000' }}>Experiment Key</th>
                                        <th style={{ color: '#000' }}>Dev</th>
                                        <th style={{ color: '#000' }}>Prod</th>
                                        <th style={{ color: '#000' }}>Bucket Key</th>
                                        <th style={{ color: '#000' }}>Override</th>
                                        <th style={{ color: '#000', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredExperiments.map(exp => {
                                        const key = exp.experiment.key;
                                        const forced = exp.forcedBucketDetails;
                                        const buckets = exp.bucketDetailsList ?? [];
                                        const isEditing = editKey === key;
                                        const isActing = actionLoading[key];

                                        return (
                                            <tr key={key}>
                                                <td style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {exp.experiment.name}
                                                </td>
                                                <td style={{ maxWidth: '190px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                                    <span title={key}>{key}</span>
                                                </td>
                                                <td>
                                                    <span className={`tag is-small ${exp.experiment.devStatus === 'RUNNING' ? 'is-success' : 'is-light'}`}>
                                                        {exp.experiment.devStatus ?? '–'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`tag is-small ${exp.experiment.prodStatus === 'RUNNING' ? 'is-success' : 'is-light'}`}>
                                                        {exp.experiment.prodStatus ?? '–'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {exp.prebucketKey
                                                        ? <span className="has-text-grey-dark">{exp.prebucketKey}</span>
                                                        : <span className="has-text-grey">–</span>}
                                                </td>
                                                <td style={{ minWidth: '200px' }}>
                                                    {isEditing ? (
                                                        <div className="field has-addons">
                                                            <div className="control">
                                                                <div className="select is-small">
                                                                    <select
                                                                        value={editBucketId}
                                                                        onChange={e => setEditBucketId(e.target.value)}
                                                                        autoFocus
                                                                    >
                                                                        <option value="">Select bucket…</option>
                                                                        {buckets.map(b => (
                                                                            <option key={b.id} value={b.id}>
                                                                                {b.key}{b.isControl ? ' (control)' : ''}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className="control">
                                                                <button type="button" className="button is-small is-success"
                                                                    disabled={!editBucketId || isActing}
                                                                    onClick={() => upsertForcedBucket(key, editBucketId)}>
                                                                    {isActing ? <SmallLoadingSpinner /> : 'Save'}
                                                                </button>
                                                            </div>
                                                            <div className="control">
                                                                <button type="button" className="button is-small is-light"
                                                                    onClick={() => setEditKey(null)}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        forced
                                                            ? <span className="tag is-link is-light">{forced.bucketKey}</span>
                                                            : <span className="has-text-grey">–</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {!isEditing && (
                                                        <>
                                                            <button type="button" className="button is-small is-light mr-1"
                                                                disabled={isActing}
                                                                onClick={() => { setEditKey(key); setEditBucketId(forced?.bucketId != null ? String(forced.bucketId) : ''); }}>
                                                                Edit
                                                            </button>
                                                            <button type="button" className="button is-small is-light"
                                                                disabled={!forced || isActing}
                                                                onClick={() => deleteForcedBucket(key)}>
                                                                {isActing ? <SmallLoadingSpinner /> : 'Clear'}
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {filteredExperiments.length === 0 && (
                                <div className="has-text-centered has-text-grey py-4 is-size-7">
                                    {searchFilter ? 'No experiments match your search.' : 'No experiments found.'}
                                </div>
                            )}
                        </div>

                        {/* Sticky bottom bar */}
                        <div style={{
                            position: 'fixed', bottom: 0, left: 0, right: 0,
                            background: '#fff', borderTop: '1px solid #dbdbdb',
                            padding: '0.75rem 1.5rem', display: 'flex',
                            justifyContent: 'flex-end', gap: '0.75rem', zIndex: 50,
                        }}>
                            <Button className="is-light" disabled={overrideCount === 0 || loading} onClick={clearAll}>
                                Clear all ({overrideCount})
                            </Button>
                            <Button className={stateCopied ? 'is-success' : 'is-dark'} onClick={copyState}>
                                <Icon icon={stateCopied ? faCheck : faCopy} />
                                <span>{stateCopied ? 'Copied!' : 'Copy state'}</span>
                            </Button>
                        </div>
                    </>
                )}
            </Container>

            {toast && (
                <div style={{ position: 'fixed', bottom: '4rem', right: '1.5rem', zIndex: 100, minWidth: '260px' }}
                    className={`notification is-${toast.type} is-light py-3 px-4`}>
                    <button className="delete" onClick={() => setToast(null)} />
                    {toast.message}
                </div>
            )}
        </>
    );
};

// Hammer runs in two contexts: standalone (dashboard host) and embedded in a
// cross-origin iframe (the legacy servlet at www.tripadvisor.com/ThorHammerV2).
// Whether we're framed can't change during the page's life, so read it once.
const isFramed = window.self !== window.top;

// In an iframe the redirect login can't complete: Azure AD won't render its login page
// inside a frame, and third-party-cookie blocking defeats silent SSO. So the embedded
// case signs in with a popup (a top-level window Azure will render), gated behind a
// one-click button because browsers block popups that aren't triggered by a user gesture.
// NOTE: MSAL still returns to window.location.pathname as its redirectUri, so that URL —
// whatever origin the iframe actually loads — must be a registered SPA redirect URI.
const FramedHammerGate = () => {
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const [signingIn, setSigningIn] = useState(false);
    const [error, setError] = useState(null);

    if (isAuthenticated) return <ThorHammerContent interaction={InteractionType.Popup} />;

    const signIn = async () => {
        setSigningIn(true);
        setError(null);
        try {
            // Request the API scope now so the access token is cached; the subsequent
            // silent acquisition inside useFetchWithMsal(Popup) then resolves from cache
            // without a network hop (which the iframe's blocked 3rd-party cookies would fail).
            await instance.loginPopup({ scopes: apiScopes });
        } catch (e) {
            setError(e?.message || 'Sign-in failed. Please try again.');
        } finally {
            setSigningIn(false);
        }
    };

    return (
        <section className="section">
            <div className="container" style={{ maxWidth: '420px' }}>
                <div className="box has-text-centered">
                    <span className="icon is-large mb-2" style={{ color: '#36e0a1' }}>
                        <Icon icon={faHammer} />
                    </span>
                    <p className="title is-4">Hammer is protected</p>
                    <p className="subtitle is-6 has-text-grey">
                        Sign in with your Microsoft account to force experiment bucket overrides.
                    </p>
                    <Button className="is-dark is-fullwidth" onClick={signIn} disabled={signingIn}>
                        <Icon icon={faLock} />
                        <span>{signingIn ? 'Signing in…' : 'Sign in with Microsoft'}</span>
                    </Button>
                    {error && <p className="help is-danger mt-3">{error}</p>}
                </div>
            </div>
        </section>
    );
};

export const ThorHammerPage = () =>
    isFramed
        ? <FramedHammerGate />
        : <ThorHammerContent interaction={InteractionType.Redirect} />;

export default ThorHammerPage;
